class AgenticReadinessScan extends HTMLElement {
  constructor() {
    super();
    this.domain = '';
    this.scanUrl = '';
    this.scanTimeout = null;
  }

  pushDataLayer(eventName, extraData = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      scan_domain: this.domain || undefined,
      ...extraData,
    });
  }

  connectedCallback() {
    this.storageKey = this.dataset.storageKey || 'agenticScanLead';
    this.scanDuration = parseInt(this.dataset.scanDuration, 10) || 2800;

    this.querySelectorAll('[data-agentic-scan-action]').forEach((control) => {
      control.addEventListener('click', this.onActionClick.bind(this));
    });

    const form = this.querySelector('.agentic-scan__form');
    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    const initialStep = this.dataset.initialStep;
    const returnedFromSuccessfulSubmit =
      initialStep === 'success' || this.isContactFormSuccessReturn();

    if (returnedFromSuccessfulSubmit) {
      this.restoreLeadFromStorage();
      this.showStep(4);
      this.pushDataLayer('agentic_scan_step4_completed');
      this.cleanContactFormSuccessUrl();
    } else if (initialStep === 'error') {
      this.restoreScanContextFromStorage();
      this.showStep(3);
    } else {
      this.showStep(0);
    }
  }

  disconnectedCallback() {
    if (this.scanTimeout) {
      clearTimeout(this.scanTimeout);
    }
  }

  onActionClick(event) {
    const control = event.currentTarget;
    const action = control.dataset.agenticScanAction;

    if (action === 'go-step') {
      const step = parseInt(control.dataset.step, 10);
      if (step === 1) {
        this.pushDataLayer('agentic_scan_step1_started');
      } else if (step === 3) {
        this.pushDataLayer('agentic_scan_step3_form_opened');
      }
      this.showStep(step);
      return;
    }

    if (action === 'start-scan') {
      this.startScan();
    }
  }

  onFormSubmit() {
    const nameInput = this.querySelector('[data-agentic-scan="name-input"]');
    const emailInput = this.querySelector('[data-agentic-scan="email-input"]');
    const hiddenUrl = this.querySelector('[data-agentic-scan="hidden-url"]');
    const hiddenBody = this.querySelector('[data-agentic-scan="hidden-body"]');

    const name = nameInput ? nameInput.value.trim() : '';
    const email = emailInput ? emailInput.value.trim() : '';

    if (!name || !email) {
      return;
    }

    this.restoreScanContextFromStorage();

    if (hiddenUrl) {
      hiddenUrl.value = this.scanUrl || this.domain;
    }

    if (hiddenBody) {
      hiddenBody.value = `Agentic Readiness Scan — ${this.domain || 'onbekend'}`;
    }

    const payload = {
      name,
      email,
      domain: this.domain,
      scanUrl: this.scanUrl,
    };

    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch (e) {
      /* sessionStorage unavailable */
    }

    this.pushDataLayer('agentic_scan_step3_form_submitted', {
      scan_email: email,
    });
  }

  startScan() {
    const urlInput = this.querySelector('[data-agentic-scan="url-input"]');
    if (!urlInput) {
      return;
    }

    const url = urlInput.value.trim();
    if (!url) {
      urlInput.focus();
      return;
    }

    this.scanUrl = url;
    this.domain = this.getDomain(url);
    this.persistScanContext();
    this.capturePartialLead();

    this.querySelectorAll('[data-agentic-scan="scan-domain"]').forEach((el) => {
      el.textContent = this.domain;
    });
    this.querySelectorAll('[data-agentic-scan="res-domain"]').forEach((el) => {
      el.textContent = this.domain;
    });

    const scanningPanel = this.querySelector('[data-agentic-scan-panel="scanning"]');
    const resultsPanel = this.querySelector('[data-agentic-scan-panel="results"]');

    if (scanningPanel) {
      scanningPanel.hidden = false;
    }
    if (resultsPanel) {
      resultsPanel.hidden = true;
    }

    this.showStep(2);
    this.pushDataLayer('agentic_scan_step2_url_submitted');

    if (this.scanTimeout) {
      clearTimeout(this.scanTimeout);
    }

    this.scanTimeout = setTimeout(() => {
      if (scanningPanel) {
        scanningPanel.hidden = true;
      }
      if (resultsPanel) {
        resultsPanel.hidden = false;
      }
      this.pushDataLayer('agentic_scan_step2_results_shown');
    }, this.scanDuration);
  }

  showStep(stepIndex) {
    this.querySelectorAll('[data-agentic-scan-step]').forEach((step) => {
      const index = parseInt(step.dataset.agenticScanStep, 10);
      const isActive = index === stepIndex;
      step.hidden = !isActive;
      step.classList.toggle('agentic-scan__step--active', isActive);
      step.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    const section = this.closest('.shopify-section') || this;
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const activeStep = this.querySelector(`[data-agentic-scan-step="${stepIndex}"]:not([hidden])`);
    if (!activeStep) {
      return;
    }

    const focusable = activeStep.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable && stepIndex > 0) {
      focusable.focus({ preventScroll: true });
    }
  }

  getDomain(url) {
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;
      return new URL(normalized).hostname.replace(/^www\./, '');
    } catch (e) {
      return url;
    }
  }

  persistScanContext() {
    try {
      const existing = sessionStorage.getItem(this.storageKey);
      const data = existing ? JSON.parse(existing) : {};
      data.domain = this.domain;
      data.scanUrl = this.scanUrl;
      sessionStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (e) {
      /* sessionStorage unavailable */
    }
  }

  capturePartialLead() {
    const endpoint = (this.dataset.captureEndpoint || '').trim();
    if (!endpoint) {
      return;
    }

    const payload = JSON.stringify({
      domain: this.domain,
      scanUrl: this.scanUrl,
      source: 'agentic-readiness-scan',
    });

    const url = endpoint.replace(/\/$/, '');

    // text/plain avoids CORS preflight; application/json + sendBeacon often fails silently cross-origin
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: payload,
      keepalive: true,
      mode: 'cors',
    }).catch(() => {
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([payload], { type: 'text/plain;charset=UTF-8' }));
        }
      } catch (e) {
        /* ignore */
      }
    });
  }

  restoreScanContextFromStorage() {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      if (!raw) {
        return;
      }
      const data = JSON.parse(raw);
      if (data.domain) {
        this.domain = data.domain;
      }
      if (data.scanUrl) {
        this.scanUrl = data.scanUrl;
      }
      this.querySelectorAll('[data-agentic-scan="res-domain"], [data-agentic-scan="scan-domain"]').forEach((el) => {
        el.textContent = this.domain;
      });
    } catch (e) {
      /* ignore */
    }
  }

  isContactFormSuccessReturn() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('contact_posted') !== 'true') {
      return false;
    }

    const form = this.querySelector('.agentic-scan__form');
    if (!form?.id) {
      return true;
    }

    const hashId = window.location.hash.replace(/^#/, '');
    return hashId === '' || hashId === form.id;
  }

  cleanContactFormSuccessUrl() {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('contact_posted')) {
        return;
      }
      url.searchParams.delete('contact_posted');
      const cleaned = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, '', cleaned);
    } catch (e) {
      /* ignore */
    }
  }

  restoreLeadFromStorage() {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      if (!raw) {
        return;
      }
      const data = JSON.parse(raw);
      const firstName = data.name ? data.name.split(' ')[0] : '';
      const thankName = this.querySelector('[data-agentic-scan="thank-name"]');
      const thankEmail = this.querySelector('[data-agentic-scan="thank-email"]');
      const thankDomain = this.querySelector('[data-agentic-scan="thank-domain"]');

      if (thankName && firstName) {
        thankName.textContent = `${firstName}!`;
      }
      if (thankEmail && data.email) {
        thankEmail.textContent = data.email;
      }
      if (thankDomain && data.domain) {
        thankDomain.textContent = data.domain;
      }

      sessionStorage.removeItem(this.storageKey);
    } catch (e) {
      /* ignore */
    }
  }
}

if (!customElements.get('agentic-readiness-scan')) {
  customElements.define('agentic-readiness-scan', AgenticReadinessScan);
}
